import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "@/lib/content";
import { EASE, EASE_IO } from "./motion";

const MobileMenu = ({ onClose }) => (
  <motion.div
    data-testid="mobile-menu"
    className="fixed inset-0 z-[60] flex flex-col justify-between bg-skaris-navy px-6 pb-10 pt-28"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: EASE_IO }}
  >
    <nav className="flex flex-col gap-2">
      {NAV.map((n, i) => (
        <div key={n.to} className="overflow-hidden">
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.25 + i * 0.07 }}>
            <Link to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="flex items-baseline gap-4 font-display text-6xl font-semibold uppercase tracking-tight text-cream">
              <span className="font-mono text-xs text-cream/50">0{i + 1}</span>{n.label}
            </Link>
          </motion.div>
        </div>
      ))}
    </nav>
    <div className="space-y-1 font-body text-sm text-cream/70">
      <a href={`mailto:${CONTACT.email}`} className="block break-all">{CONTACT.email}</a>
      <a href={CONTACT.phoneHref} className="block">{CONTACT.phone}</a>
    </div>
  </motion.div>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  return (
    <>
      <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,backdrop-filter,border-color] duration-500 ${scrolled && !open ? "border-b border-cream/10 bg-skaris-ink/60 backdrop-blur-xl" : "border-b border-transparent"}`}>
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 md:px-10">
          <Link to="/" data-testid="nav-logo" className="text-cream"><Logo /></Link>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n, i) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `group relative px-4 py-2 font-body text-[13px] uppercase tracking-[0.16em] transition-colors ${isActive ? "text-cream" : "text-cream/60 hover:text-cream"}`}>
                {({ isActive }) => (
                  <>
                    <span className="mr-1.5 font-mono text-[10px] text-skaris-sky">0{i + 1}</span>{n.label}
                    <span className={`absolute inset-x-4 bottom-1 h-px origin-left bg-skaris-sky transition-transform duration-500 ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <Link to="/contact" data-testid="nav-cta" className="hidden rounded-full border border-cream/25 px-5 py-2.5 font-body text-xs uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:bg-cream hover:text-skaris-ink md:inline-flex">
            Start a project
          </Link>
          <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setOpen((o) => !o)} className="relative flex h-11 w-11 items-center justify-center rounded-full border border-cream/25 md:hidden">
            <span className={`absolute h-px w-5 bg-cream transition-transform duration-500 ${open ? "rotate-45" : "-translate-y-1"}`} />
            <span className={`absolute h-px w-5 bg-cream transition-transform duration-500 ${open ? "-rotate-45" : "translate-y-1"}`} />
          </button>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
